import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

type HubServer = 'HubGeneric';
export type HubKey =
	| 'SESSION_TIMER_REFRESH'
	| 'ADMIN'
	| 'FILE_EXPORT'
	| 'REFETCH_NEEDED'
	| 'CHAT'
	| 'WHITEBOARD'
	| 'SOURCE_KNOWLEDGE_PROGRESS'
	| 'EXPERIENCE_UPDATE_DEFAULT';
export type HubConnectionState = 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' | 'RECONNECTING';

type HubServerConnectionResponse = {
	ConnectionId: string;
};

const isHubServerConnectionResponse = (obj: unknown): obj is HubServerConnectionResponse => {
	return typeof obj === 'object' && obj !== null && 'ConnectionId' in obj;
};

type HubServerMessage = {
	Content: string;
	HubKey: HubKey;
	HubServerName: HubServer;
};

const isHubServerMessage = (obj: unknown): obj is HubServerMessage => {
	return typeof obj === 'object' && obj !== null && 'Content' in obj && 'HubKey' in obj && 'HubServerName' in obj;
};

type HubServerAdminMessage = {
	AccessModuleKey: string;
	Content: unknown;
};

const isHubServerAdminMessage = (obj: unknown): obj is HubServerAdminMessage => {
	return typeof obj === 'object' && obj !== null && 'AccessModuleKey' in obj && 'Content' in obj;
};

type SignalRContextType = {
	subscribe: (
		hubServer: HubServer,
		hubKey: HubKey,
		group: string | null,
		onMessage: (content: unknown) => void,
		onConnectionStateChanged?: (newState: HubConnectionState) => void
	) => void;
	unsubscribe: (hubServer: HubServer, hubKey: HubKey, group: string | null) => void;
	sendMessage: (key: HubKey, message: unknown) => void;
	connectionId: string | null;
	connectionState: HubConnectionState;
};

export const SignalRContext = createContext<SignalRContextType>({
	subscribe: () => {},
	unsubscribe: () => {},
	sendMessage: () => {},
	connectionId: null,
	connectionState: 'CONNECTING',
});

const isHubConnectionState = (state: unknown): state is HubConnectionState => {
	return state === 'CONNECTING' || state === 'CONNECTED' || state === 'DISCONNECTED' || state === 'RECONNECTING';
};

const getSubscribeKey = (hubServer: HubServer, hubKey: HubKey, group: string | null) => {
	const groupString = group ? `_${group}` : '';
	return `${hubServer}_${hubKey}${groupString}`.toLowerCase();
};

const sendToServer = (hubServerMessage: HubServerMessage) => {
	//console.log('Sending to server via window dispatch event:', hubServerMessage);
	window.dispatchEvent(new CustomEvent('HubClientSendServerMsg', { detail: { d: hubServerMessage } }));
};

type SignalRContextProviderProps = {
	children: React.ReactNode;
};

export const SignalRContextProvider = ({ children }: SignalRContextProviderProps) => {
	const subscriptionsRef = useRef<{
		[subscribeKey: string]: {
			onMessage: (content: unknown) => void;
			onConnectionStateChanged?: (newState: HubConnectionState) => void;
		}[];
	}>({});

	const [connectionId, setConnectionId] = useState<string | null>(null);
	const [connectionState, setConnectionState] = useState<HubConnectionState>('CONNECTING');

	const sendMessage = useCallback((key: HubKey, message: unknown) => {
		//console.log('sendMessage (useCallback) called with key:', key, 'and message:', message);
		sendToServer({
			Content: JSON.stringify(message),
			HubKey: key,
			HubServerName: 'HubGeneric',
		});
	}, []);

	const sendToAdminServer = useCallback((key: string, message: string) => {
		sendToServer({
			Content: JSON.stringify({
				Key: key,
				Message: message,
			}),
			HubKey: 'ADMIN',
			HubServerName: 'HubGeneric',
		});
	}, []);

	const subscribe = useCallback(
		(
			hubServer: HubServer,
			hubKey: HubKey,
			group: string | null,
			onMessage: (content: unknown) => void,
			onConnectionStateChanged?: (newState: HubConnectionState) => void
		) => {
			const subscribeKey = getSubscribeKey(hubServer, hubKey, group);
			subscriptionsRef.current[subscribeKey] = [
				...(subscriptionsRef.current[subscribeKey] || []),
				{ onMessage, onConnectionStateChanged },
			];
			if (group) sendToAdminServer('JOINGROUP', group);
		},
		[sendToAdminServer]
	);

	const unsubscribe = useCallback((hubServer: HubServer, hubKey: HubKey, group: string | null) => {
		const subscribeKey = getSubscribeKey(hubServer, hubKey, group);
		delete subscriptionsRef.current[subscribeKey];
	}, []);

	useEffect(() => {
		const handleEvent = ((event: CustomEvent) => {
			if (!event.detail) return;

			//console.log('REACT SignalR.serverMessage event received:', event);
			//if(event.detail.d.)
			if (isHubServerConnectionResponse(event.detail.d)) {
				setConnectionId(event.detail.d.ConnectionId);
				return;
			}
			if (isHubServerMessage(event.detail.d)) {
				const hubServerMessage = event.detail.d as HubServerMessage;
				//console.log('Received HubServerMessage handleEvent init', hubServerMessage);
				if (hubServerMessage.HubKey === 'SESSION_TIMER_REFRESH') {
					//console.log('SESSION_TIMER_REFRESH not implemented yet');
					return;
				}
				const objHubServerMessage = JSON.parse(hubServerMessage.Content);
				if (isHubServerAdminMessage(objHubServerMessage)) {
					//console.log('Received HubServerAdminMessage', objHubServerMessage);
					const subscribeKey = getSubscribeKey(
						hubServerMessage.HubServerName,
						hubServerMessage.HubKey,
						objHubServerMessage.AccessModuleKey //not exactly straight forward but we have to work around how it works in angularjs
					);
					//console.log('subscribeKey', subscribeKey);
					if (subscriptionsRef.current[subscribeKey]) {
						subscriptionsRef.current[subscribeKey].forEach((subsrc) => {
							//console.log('Invoking callback for admin message with content', objHubServerMessage.Content);
							subsrc.onMessage(objHubServerMessage.Content);
						});
					}
				} else {
					//console.log('Received HubServerMessage', objHubServerMessage);
					//only works without group for now
					const subscribeKey = getSubscribeKey(hubServerMessage.HubServerName, hubServerMessage.HubKey, null);
					//console.log('subscribeKey for non-admin message:', subscribeKey);
					if (subscriptionsRef.current[subscribeKey]) {
						subscriptionsRef.current[subscribeKey].forEach((subsrc) => subsrc.onMessage(objHubServerMessage));
					}
				}
			} else {
				console.log('SignalR.serverMessage react: event.detail.d does not satisfy HubServerMessage', event.detail.d);
			}
		}) as EventListener;

		const handleConnectionStateChangeEvent = ((event: CustomEvent) => {
			const newState = event.detail?.d;
			if (!isHubConnectionState(newState)) {
				console.error('Ignoring invalid SignalR connection state:', newState);
				return;
			}

			setConnectionState(newState);
			if (newState === 'DISCONNECTED') {
				setConnectionId(null);
			}

			Object.keys(subscriptionsRef.current).forEach((key) => {
				subscriptionsRef.current[key].forEach((subsrc) => {
					if (subsrc.onConnectionStateChanged) {
						subsrc.onConnectionStateChanged(newState);
					}
				});
			});
		}) as EventListener;

		document.addEventListener('SignalR.serverMessage', handleEvent);
		document.addEventListener('SignalR.connectionStateChange', handleConnectionStateChangeEvent);
		document.dispatchEvent(new CustomEvent('SignalR.requestConnectionState'));

		return () => {
			document.removeEventListener('SignalR.serverMessage', handleEvent);
			document.removeEventListener('SignalR.connectionStateChange', handleConnectionStateChangeEvent);
		};
	}, []);

	return (
		<SignalRContext.Provider value={{ subscribe, unsubscribe, sendMessage, connectionId, connectionState }}>
			{children}
		</SignalRContext.Provider>
	);
};

export const useSignalRContext = () => {
	const context = useContext(SignalRContext);
	if (!context) {
		throw new Error('useSignalRContext must be used within a SignalRContextProvider');
	}
	return context;
};
